import { useConfigContext } from '@fe-core';
import { HeaderConfigItemSchema, HeaderConsumerSchema } from '@fe-core-events';
import { useTeamviewHeaderItem } from '@fe-learner-common/hooks/useTeamviewHeaderItem';
import { deepMerge } from '@fe-learner-common/utils/merge';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

type HeaderMenuItem = {
  order: number;
} & HeaderConfigItemSchema;

type ActiveItem =
  | 'home'
  | 'courses'
  | 'microlearnings'
  | 'catalog'
  | 'teamview'
  | null;

type UseHeaderConfigOptions = {
  enabled: boolean;
  activeItem?: ActiveItem;
} & Partial<Omit<HeaderConsumerSchema, 'items'>>;

const createDefaultHeaderConfig = ({
  search = { enabled: true, type: 'client' },
  hasSearchWebFeature,
}: {
  search?: HeaderConsumerSchema['search'];
  hasSearchWebFeature: boolean;
}): Omit<HeaderConsumerSchema, 'items'> => ({
  search: {
    ...search,
    enabled: search.enabled && hasSearchWebFeature,
  },
  chat: {
    enabled: true,
  },
  notifications: {
    enabled: true,
  },
  goto: {
    enabled: true,
  },
});

export const buildPath = (basePath: string = '', path: string): string => {
  if (!basePath || basePath === '/') {
    return path;
  }

  const paths = basePath.match(/(\/[^/]+)?/g) || [];
  if (paths.length > 1) {
    const [rootPath] = paths;
    return `${rootPath}${path}`;
  }
  return '';
};

export const useHeaderConfig = ({
  enabled,
  activeItem = null,
  search,
  ...headerConfig
}: UseHeaderConfigOptions): HeaderConsumerSchema | null => {
  const intl = useIntl();
  const { config, extension } = useConfigContext();
  const isLoggedIn = !!config?.loggedIn;
  const teamviewHeaderItem = useTeamviewHeaderItem({
    active: activeItem === 'teamview',
  });
  const hasSearchWebFeature =
    !!config?.webfeatures?.['fe-core-header-ng-search-learner'];

  const headerItems: HeaderConfigItemSchema[] | null = useMemo(() => {
    if (!enabled || !isLoggedIn) {
      return null;
    }

    const { basename } = extension?.microfrontend || {};

    const items: HeaderMenuItem[] = [
      ...(!!config?.webfeatures?.['fe-learner-start-page']
        ? [
            {
              order: 0,
              type: 'link' as const,
              active: activeItem === 'home',
              name: intl.formatMessage({ id: 'learner.home.title' }),
              value: buildPath(basename, '/home'),
            },
          ]
        : []),
      {
        order: 1,
        type: 'link',
        active: activeItem === 'courses',
        name: intl.formatMessage({ id: 'courses.title' }),
        value: buildPath(basename, '/learner/courses'),
      },
      ...(!!config?.webfeatures?.['fe-learner-microlearnings']
        ? [
            {
              order: 2,
              type: 'link' as const,
              active: activeItem === 'microlearnings',
              name: intl.formatMessage({ id: 'microlearnings.title' }),
              value: buildPath(basename, '/microlearnings'),
            },
          ]
        : []),
      ...(!!config?.webfeatures?.['coursecatalog']
        ? [
            {
              order: 3,
              type: 'link' as const,
              active: activeItem === 'catalog',
              name: intl.formatMessage({ id: 'courses.header.menu.catalog' }),
              value: buildPath(basename, '/catalog'),
            },
          ]
        : []),
      ...(teamviewHeaderItem?.enabled ? [teamviewHeaderItem.item] : []),
    ];

    return items
      .toSorted((a, b) => a.order - b.order)
      .map(({ order: _, ...rest }) => rest);
  }, [
    extension,
    enabled,
    intl,
    isLoggedIn,
    config,
    teamviewHeaderItem,
    activeItem,
  ]);

  return useMemo(() => {
    if (!enabled || !isLoggedIn) {
      return null;
    }

    const defaultConfig = createDefaultHeaderConfig({
      search,
      hasSearchWebFeature,
    });

    const mergedConfig =
      Object.keys(headerConfig).length > 0
        ? deepMerge(defaultConfig, headerConfig)
        : defaultConfig;

    return {
      ...mergedConfig,
      items: headerItems || [],
    };
  }, [
    enabled,
    isLoggedIn,
    search,
    hasSearchWebFeature,
    headerConfig,
    headerItems,
  ]);
};
